import { useState } from "react";
import { motion } from "framer-motion";
import { Info } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";

/* Figures transcribed verbatim from the TAVONX TNX Genesis Tokenomics Proposal */
const ALLOCATIONS = [
    { name: "Genesis Sale", amount: 100000, pct: 10, color: "#00D1FF" },
    { name: "UXT Holder Genesis", amount: 50000, pct: 5, color: "#8B5CF6" },
    { name: "Exchange Liquidity", amount: 150000, pct: 15, color: "#168BFF" },
    { name: "Exchange Ecosystem", amount: 200000, pct: 20, color: "#2563EB" },
    { name: "Staking / Utility", amount: 150000, pct: 15, color: "#A78BFA" },
    { name: "Treasury", amount: 200000, pct: 20, color: "#38BDF8" },
    { name: "Team / Development", amount: 100000, pct: 10, color: "#94A3B8" },
];

const MAX_SUPPLY = 1000000;
const TOTAL_ALLOCATED = ALLOCATIONS.reduce((s, a) => s + a.amount, 0);
const fmt = (n) => n.toLocaleString("en-US");
const slug = (n) => n.toLowerCase().replace(/[^a-z]+/g, "-").replace(/(^-|-$)/g, "");

export default function Tokenomics() {
    const [active, setActive] = useState(null);
    const R = 44;
    const C = 2 * Math.PI * R;
    const GAP = 1.8;

    let acc = 0;
    const segs = ALLOCATIONS.map((a) => {
        const seg = { ...a, frac: a.amount / TOTAL_ALLOCATED, rot: (acc / TOTAL_ALLOCATED) * 360 };
        acc += a.amount;
        return seg;
    });
    const current = active !== null ? ALLOCATIONS[active] : null;

    return (
        <section
            id="tokenomics"
            data-testid="tokenomics-section"
            className="relative overflow-hidden bg-base py-24 lg:py-32"
        >
            <div className="pointer-events-none absolute left-[-10%] top-[30%] h-[50vh] w-[40vw] rounded-full bg-[radial-gradient(circle,rgba(37,99,235,0.07),transparent_70%)]" />

            <div className="relative mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
                <div className="grid items-center gap-16 lg:grid-cols-2 lg:gap-20">
                    {/* donut */}
                    <Reveal className="flex justify-center order-1">
                        <div
                            data-testid="tokenomics-donut"
                            className="relative"
                            style={{ width: "min(430px, 82vw)", height: "min(430px, 82vw)" }}
                        >
                            <div className="absolute inset-[8%] rounded-full bg-[radial-gradient(circle,rgba(0,209,255,0.12),rgba(139,92,246,0.05)_55%,transparent_75%)]" />
                            <svg viewBox="0 0 100 100" className="relative h-full w-full -rotate-90">
                                <circle cx="50" cy="50" r={R} fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="7.5" />
                                {segs.map((s, i) => {
                                    const segLen = s.frac * C - GAP;
                                    const isActive = active === i;
                                    const dim = active !== null && !isActive;
                                    return (
                                        <motion.circle
                                            key={s.name}
                                            cx="50"
                                            cy="50"
                                            r={R}
                                            fill="none"
                                            stroke={s.color}
                                            strokeWidth={isActive ? 9.5 : 7.5}
                                            strokeLinecap="butt"
                                            strokeDasharray={`${segLen} ${C - segLen}`}
                                            transform={`rotate(${s.rot} 50 50)`}
                                            initial={{ strokeDashoffset: segLen, opacity: 0 }}
                                            whileInView={{ strokeDashoffset: 0, opacity: dim ? 0.28 : 1 }}
                                            viewport={{ once: true, amount: 0.3 }}
                                            transition={{
                                                duration: 1.1,
                                                delay: 0.15 + i * 0.1,
                                                ease: [0.22, 1, 0.36, 1],
                                                opacity: { duration: 0.3, delay: 0 },
                                            }}
                                            style={{
                                                cursor: "pointer",
                                                filter: isActive ? `drop-shadow(0 0 6px ${s.color})` : "none",
                                            }}
                                            onMouseEnter={() => setActive(i)}
                                            onMouseLeave={() => setActive(null)}
                                        />
                                    );
                                })}
                            </svg>
                            <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                                {current ? (
                                    <motion.div
                                        key={current.name}
                                        initial={{ opacity: 0, scale: 0.94 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        transition={{ duration: 0.25 }}
                                        className="text-center"
                                    >
                                        <div className="font-mono text-2xl font-semibold tabular-nums" style={{ color: current.color }}>
                                            {fmt(current.amount)}
                                        </div>
                                        <div className="mx-auto mt-1.5 max-w-[120px] font-mono text-[9px] uppercase leading-relaxed tracking-[0.24em] text-slate-400">
                                            {current.name} · {current.pct}%
                                        </div>
                                    </motion.div>
                                ) : (
                                    <div className="text-center" data-testid="tokenomics-max-supply">
                                        <div className="font-grotesk text-3xl font-bold tabular-nums text-white sm:text-4xl">
                                            {fmt(MAX_SUPPLY)}
                                        </div>
                                        <div className="mt-2 font-mono text-[9px] uppercase tracking-[0.3em] text-cyber">
                                            Max supply · TNX
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </Reveal>

                    {/* legend + copy */}
                    <div className="order-2">
                        <Reveal>
                            <Eyebrow>TNX Tokenomics</Eyebrow>
                        </Reveal>
                        <Reveal delay={0.08}>
                            <h2 className="font-grotesk text-2xl font-bold uppercase leading-tight tracking-tight text-white sm:text-3xl lg:text-4xl">
                                Aligned for the <span className="text-grad-blue">long term.</span>
                            </h2>
                        </Reveal>
                        <Reveal delay={0.14}>
                            <p className="mt-5 max-w-md text-base leading-relaxed text-slate-400">
                                A maximum supply of 1,000,000 TNX, allocated across seven
                                functions of the TAVONX ecosystem.
                            </p>
                        </Reveal>

                        <Reveal delay={0.2}>
                            <div className="mt-9 space-y-1">
                                {ALLOCATIONS.map((a, i) => (
                                    <div
                                        key={a.name}
                                        data-testid={`tokenomics-row-${slug(a.name)}`}
                                        onMouseEnter={() => setActive(i)}
                                        onMouseLeave={() => setActive(null)}
                                        className={`cursor-pointer rounded-md border px-4 py-3 transition-all duration-300 ${
                                            active === i
                                                ? "border-white/15 bg-white/[0.04]"
                                                : "border-transparent"
                                        }`}
                                    >
                                        <div className="flex items-center justify-between gap-4">
                                            <div className="flex items-center gap-3">
                                                <span
                                                    className="h-2.5 w-2.5 shrink-0 rounded-full transition-all duration-300"
                                                    style={{
                                                        background: a.color,
                                                        boxShadow: active === i ? `0 0 10px ${a.color}` : "none",
                                                    }}
                                                />
                                                <span className="font-grotesk text-[13px] font-medium uppercase tracking-[0.12em] text-slate-200">
                                                    {a.name}
                                                </span>
                                            </div>
                                            <div className="flex items-center gap-4">
                                                <span className="font-mono text-sm tabular-nums text-white">
                                                    {fmt(a.amount)}
                                                </span>
                                                <span className="w-9 text-right font-mono text-xs text-slate-500">
                                                    {a.pct}%
                                                </span>
                                            </div>
                                        </div>
                                        <div className="mt-2.5 h-0.5 w-full rounded bg-white/[0.05]">
                                            <motion.div
                                                className="h-full rounded"
                                                style={{ background: a.color }}
                                                initial={{ width: 0 }}
                                                whileInView={{ width: `${(a.amount / TOTAL_ALLOCATED) * 100}%` }}
                                                viewport={{ once: true, amount: 0.4 }}
                                                transition={{ duration: 1, delay: 0.25 + i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </Reveal>

                        <Reveal delay={0.3}>
                            <div
                                data-testid="tokenomics-note"
                                className="mt-8 flex items-start gap-3 rounded-lg border border-white/[0.07] bg-panel/40 p-5"
                            >
                                <Info className="mt-0.5 h-4 w-4 shrink-0 text-cyber" strokeWidth={1.5} />
                                <div>
                                    <p className="text-[13px] leading-relaxed text-slate-300">
                                        This allocation is designed to support long-term growth,
                                        ecosystem development and community alignment.
                                    </p>
                                    <p className="mt-2 font-mono text-[10px] leading-relaxed tracking-wide text-slate-600">
                                        Source: TAVONX TNX Genesis Tokenomics Proposal — figures as
                                        published, subject to final documentation. Not financial advice.
                                    </p>
                                </div>
                            </div>
                        </Reveal>
                    </div>
                </div>
            </div>
        </section>
    );
}
